package com.iregained.eventapp.components.sections.event_details.sections

import androidx.compose.runtime.*
import com.iregained.eventapp.components.sections.event_details.styles.ModernInputStyle
import com.iregained.eventapp.components.sections.event_details.styles.RegistrationCardStyle
import com.iregained.eventapp.components.sections.event_details.styles.RegistrationContainerStyle
import com.iregained.eventapp.components.widgets.SectionHeader
import com.iregained.eventapp.theme.SubheadlineTextStyle
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit
import kotlin.js.json

// Native JS string encoder for the fetch payload
external fun encodeURIComponent(uriComponent: String): String

@Composable
private fun Modifier.slideUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this
        .opacity(if (isVisible) 1 else 0)
        .styleModifier {
            property("transform", if (isVisible) "translateY(0)" else "translateY(30px)")
            property("will-change", "opacity, transform")
        }
        .transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun EventRegistrationSection(data: EventRegistrationFormData = defaultEventRegistrationData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "event-registration-section"

    var isVisible by remember { mutableStateOf(false) }

    val ctx = rememberPageContext()
    val typeParam = ctx.route.params["type"]

    // 2. Tie the initial state to the URL parameter.
    // Using remember(typeParam) ensures it updates if the user clicks the hero button again.
    var isPatient by remember(typeParam) {
        mutableStateOf(typeParam != "clinic")
    }

    var formSubmitted by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }

    Column(
        modifier = RegistrationContainerStyle.toModifier().id(targetId)
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                    ref { element: HTMLElement ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SectionHeader(
                    text = data.title,
                    fontSize = 32.px,
                    lineWidthPercent = 50.percent,
                    modifier = Modifier.margin(bottom = 16.px).slideUp(isVisible, 0),
                    alignment = Alignment.CenterHorizontally
                )

                Div(
                    attrs = SubheadlineTextStyle.toModifier().maxWidth(800.px).fontSize(18.px)
                        .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                        .textAlign(TextAlign.Center).margin(bottom = 40.px).slideUp(isVisible, 150).toAttrs()
                ) {
                    Text(data.description)
                }

                Box(modifier = RegistrationCardStyle.toModifier().slideUp(isVisible, 300)) {
                    Column(modifier = Modifier.fillMaxWidth()) {

                        // Segmented Control Toggle
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .backgroundColor(palette.custom.neutralLight)
                                .borderRadius(8.px)
                                .padding(4.px)
                                .margin(bottom = 24.px)
                        ) {
                            val activeMod = Modifier.backgroundColor(Colors.White).color(palette.custom.brandPrimary)
                                .boxShadow(0.px, 2.px, 4.px, color = rgba(0, 0, 0, 0.1))
                            val inactiveMod =
                                Modifier.backgroundColor(Colors.Transparent).color(palette.custom.darkText)

                            Box(
                                modifier = Modifier.weight(1f).padding(10.px).borderRadius(6.px).cursor(Cursor.Pointer)
                                    .onClick { isPatient = true }.then(if (isPatient) activeMod else inactiveMod),
                                contentAlignment = Alignment.Center
                            ) { SpanText("I am a Patient", modifier = Modifier.fontWeight(FontWeight.SemiBold)) }

                            Box(
                                modifier = Modifier.weight(1f).padding(10.px).borderRadius(6.px).cursor(Cursor.Pointer)
                                    .onClick { isPatient = false }.then(if (!isPatient) activeMod else inactiveMod),
                                contentAlignment = Alignment.Center
                            ) { SpanText("I am a Clinic", modifier = Modifier.fontWeight(FontWeight.SemiBold)) }
                        }

                        // DOM-based Form
                        Form(attrs = Modifier.fillMaxWidth().toAttrs {
                            attr("action", data.formProcessingEndpoint)
                            attr("method", "POST")
                            onSubmit { e ->
                                e.preventDefault()
                                isSubmitting = true

                                // 1. Grab honeypot
                                val honeypot =
                                    (document.getElementById("event_website_url_honeypot") as? HTMLInputElement)?.value
                                        ?: ""
                                if (honeypot.isNotEmpty()) {
                                    isSubmitting = false
                                    formSubmitted = true
                                    return@onSubmit
                                }

                                // 2. Extract DOM values
                                val fullName =
                                    (document.getElementById("event_full_name") as? HTMLInputElement)?.value ?: ""
                                val email = (document.getElementById("event_email") as? HTMLInputElement)?.value ?: ""
                                val clinicName = if (!isPatient) {
                                    (document.getElementById("event_clinic_name") as? HTMLInputElement)?.value ?: ""
                                } else ""
                                val address =
                                    (document.getElementById("event_address") as? HTMLInputElement)?.value ?: ""
                                val city = (document.getElementById("event_city") as? HTMLInputElement)?.value ?: ""
                                val province =
                                    (document.getElementById("event_province") as? HTMLSelectElement)?.value ?: ""
                                val postalCode =
                                    (document.getElementById("event_postal_code") as? HTMLInputElement)?.value ?: ""
                                val message =
                                    (document.getElementById("event_message") as? HTMLTextAreaElement)?.value ?: ""
                                val consentGiven =
                                    (document.getElementById("event_consent") as? HTMLInputElement)?.checked ?: false

                                // 3. Build payload matching the GAS format
                                val formId = if (isPatient) "patient_registration" else "clinic_registration"

                                val formData = "form_id=${encodeURIComponent(formId)}" +
                                        "&website_url_honeypot=${encodeURIComponent(honeypot)}" +
                                        "&full_name=${encodeURIComponent(fullName)}" +
                                        "&email=${encodeURIComponent(email)}" +
                                        "&clinic_name=${encodeURIComponent(clinicName)}" +
                                        "&address=${encodeURIComponent(address)}" +
                                        "&city=${encodeURIComponent(city)}" +
                                        "&province=${encodeURIComponent(province)}" +
                                        "&postal_code=${encodeURIComponent(postalCode)}" +
                                        "&message=${encodeURIComponent(message)}" +
                                        "&consent=${consentGiven}"

                                val requestInit = RequestInit(
                                    method = "POST",
                                    mode = org.w3c.fetch.RequestMode.NO_CORS,
                                    headers = json("Content-Type" to "application/x-www-form-urlencoded"),
                                    body = formData
                                )

                                // 4. Execute Fetch
                                window.fetch(data.formProcessingEndpoint, requestInit).then {
                                    isSubmitting = false
                                    formSubmitted = true

                                    // Clear form fields after success
                                    (document.getElementById("event_full_name") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_email") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_clinic_name") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_address") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_city") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_province") as? HTMLSelectElement)?.value = ""
                                    (document.getElementById("event_postal_code") as? HTMLInputElement)?.value = ""
                                    (document.getElementById("event_message") as? HTMLTextAreaElement)?.value = ""
                                    (document.getElementById("event_consent") as? HTMLInputElement)?.checked = false
                                }.catch {
                                    isSubmitting = false
                                    window.alert(data.alertErrorMessage)
                                }
                            }
                        }) {
                            // Hidden Honeypot Input
                            Input(
                                type = InputType.Text,
                                attrs = Modifier.opacity(0).position(Position.Absolute).top(0.px).left(0.px).width(1.px)
                                    .height(1.px).styleModifier {
                                        property("pointer-events", "none")
                                    }.toAttrs {
                                        attr("name", "website_url_honeypot")
                                        attr("id", "event_website_url_honeypot")
                                        attr("tabindex", "-1")
                                        attr("autocomplete", "off")
                                    }
                            )

                            SimpleGrid(
                                numColumns(1, sm = 2),
                                modifier = Modifier.fillMaxWidth().gap(16.px).margin(bottom = 16.px)
                            ) {
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium)
                                            .toAttrs { attr("for", "event_full_name") }) { Text(data.fullNameLabel) }
                                    Input(type = InputType.Text, attrs = ModernInputStyle.toAttrs {
                                        placeholder(data.fullNamePlaceholder)
                                        attr("id", "event_full_name")
                                        attr("name", "full_name")
                                        attr("required", "true")
                                    })
                                }
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium)
                                            .toAttrs { attr("for", "event_email") }) { Text(data.emailLabel) }
                                    Input(type = InputType.Email, attrs = ModernInputStyle.toAttrs {
                                        placeholder(data.emailPlaceholder)
                                        attr("id", "event_email")
                                        attr("name", "email")
                                        attr("required", "true")
                                    })
                                }
                            }

                            if (!isPatient) {
                                Column(modifier = Modifier.fillMaxWidth().margin(bottom = 16.px)) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium).toAttrs {
                                                attr(
                                                    "for",
                                                    "event_clinic_name"
                                                )
                                            }) { Text(data.clinicNameLabel) }
                                    Input(type = InputType.Text, attrs = ModernInputStyle.toAttrs {
                                        placeholder(data.clinicNamePlaceholder)
                                        attr("id", "event_clinic_name")
                                        attr("name", "clinic_name")
                                        attr("required", "true")
                                    })
                                }
                            }

                            Column(modifier = Modifier.fillMaxWidth().margin(bottom = 16.px)) {
                                Label(
                                    attrs = Modifier.fontSize(14.px).margin(bottom = 6.px).fontWeight(FontWeight.Medium)
                                        .toAttrs { attr("for", "event_address") }) { Text(data.addressLabel) }
                                Input(type = InputType.Text, attrs = ModernInputStyle.toAttrs {
                                    placeholder(data.addressPlaceholder)
                                    attr("id", "event_address")
                                    attr("name", "address")
                                    attr("required", "true")
                                })
                            }

                            SimpleGrid(
                                numColumns(1, sm = 3),
                                modifier = Modifier.fillMaxWidth().gap(16.px).margin(bottom = 16.px)
                            ) {
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium)
                                            .toAttrs { attr("for", "event_city") }) { Text(data.cityLabel) }
                                    Input(type = InputType.Text, attrs = ModernInputStyle.toAttrs {
                                        placeholder(data.cityPlaceholder)
                                        attr("id", "event_city")
                                        attr("name", "city")
                                        attr("required", "true")
                                    })
                                }
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium)
                                            .toAttrs { attr("for", "event_province") }) { Text(data.provinceLabel) }
                                    Select(attrs = ModernInputStyle.toAttrs {
                                        attr("id", "event_province")
                                        attr("name", "province")
                                        attr("required", "true")
                                    }) {
                                        Option(value = "", attrs = { selected(); disabled() }) { Text("Select...") }
                                        Option(value = "AB") { Text("Alberta") }
                                        Option(value = "BC") { Text("British Columbia") }
                                        Option(value = "MB") { Text("Manitoba") }
                                        Option(value = "NB") { Text("New Brunswick") }
                                        Option(value = "NL") { Text("Newfoundland and Labrador") }
                                        Option(value = "NS") { Text("Nova Scotia") }
                                        Option(value = "ON") { Text("Ontario") }
                                        Option(value = "PE") { Text("Prince Edward Island") }
                                        Option(value = "QC") { Text("Quebec") }
                                        Option(value = "SK") { Text("Saskatchewan") }
                                        Option(value = "NT") { Text("Northwest Territories") }
                                        Option(value = "NU") { Text("Nunavut") }
                                        Option(value = "YT") { Text("Yukon") }
                                        Option(value = "OTHER") { Text("Other") }
                                    }
                                }
                                Column(modifier = Modifier.fillMaxWidth()) {
                                    Label(
                                        attrs = Modifier.fontSize(14.px).margin(bottom = 6.px)
                                            .fontWeight(FontWeight.Medium).toAttrs {
                                                attr(
                                                    "for",
                                                    "event_postal_code"
                                                )
                                            }) { Text(data.postalCodeLabel) }
                                    Input(type = InputType.Text, attrs = ModernInputStyle.toAttrs {
                                        placeholder(data.postalCodePlaceholder)
                                        attr("id", "event_postal_code")
                                        attr("name", "postal_code")
                                        attr("required", "true")
                                    })
                                }
                            }

                            Column(modifier = Modifier.fillMaxWidth().margin(bottom = 24.px)) {
                                Label(
                                    attrs = Modifier.fontSize(14.px).margin(bottom = 6.px).fontWeight(FontWeight.Medium)
                                        .toAttrs { attr("for", "event_message") }) { Text(data.messageLabel) }
                                TextArea(attrs = ModernInputStyle.toAttrs {
                                    placeholder(data.messagePlaceholder)
                                    attr("id", "event_message")
                                    attr("name", "message")
                                    style { property("resize", "vertical"); minHeight(100.px) }
                                })
                            }

                            Row(
                                modifier = Modifier.fillMaxWidth().margin(bottom = 24.px),
                                verticalAlignment = Alignment.Top
                            ) {
                                Input(
                                    type = InputType.Checkbox,
                                    attrs = Modifier.size(16.px).margin(top = 4.px, right = 12.px)
                                        .cursor(Cursor.Pointer).toAttrs {
                                            attr("id", "event_consent")
                                            attr("name", "consent")
                                            attr("required", "true")
                                        }
                                )
                                Label(
                                    attrs = Modifier.cursor(Cursor.Pointer).lineHeight(1.5).fontSize(12.px)
                                        .color(palette.custom.darkText).toAttrs {
                                            attr("for", "event_consent")
                                        }
                                ) {
                                    Text(if (isPatient) data.patientConsentText else data.clinicConsentText)
                                }
                            }

                            Button(
                                attrs = Modifier
                                    .fillMaxWidth()
                                    .padding(14.px)
                                    .backgroundColor(palette.custom.brandPrimary)
                                    .color(Colors.White)
                                    .borderRadius(8.px)
                                    .border(0.px)
                                    .opacity(if (isSubmitting) 0.6f else 1f)
                                    .cursor(if (isSubmitting) Cursor.NotAllowed else Cursor.Pointer)
                                    .fontWeight(FontWeight.Bold)
                                    .toAttrs {
                                        attr("type", "submit")
                                        if (isSubmitting) attr("disabled", "true")
                                    }
                            ) {
                                Text(if (isSubmitting) data.buttonSubmittingText else data.buttonText)
                            }
                        }
                    }
                }
            }
        }
    }

    // Success Modal Overlay
    if (formSubmitted) {
        Box(
            modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize()
                .zIndex(1000).backgroundColor(Color.argb(0.6f, 0f, 0f, 0f))
                .onClick { formSubmitted = false }, contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier.width(90.percent).maxWidth(500.px)
                    .backgroundColor(Colors.White).borderRadius(8.px).padding(32.px)
                    .boxShadow(0.px, 10.px, 30.px, color = Color.argb(0.3f, 0f, 0f, 0f))
                    .onClick { it.stopPropagation() },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(
                    modifier = Modifier.size(64.px).borderRadius(50.percent)
                        .backgroundColor(Color.rgb(0xE8F5E9)).margin(bottom = 16.px),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = data.modalIconText,
                        modifier = Modifier.color(Color.rgb(0x2E8B57)).fontSize(32.px)
                            .fontWeight(FontWeight.Bold)
                    )
                }
                SpanText(
                    text = data.modalTitle,
                    modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold)
                        .color(Color.rgb(0x333333)).margin(bottom = 8.px)
                )
                SpanText(
                    text = data.modalDescription,
                    modifier = Modifier.fontSize(16.px).lineHeight(1.5)
                        .textAlign(TextAlign.Center).color(Color.rgb(0x666666))
                        .margin(bottom = 24.px)
                )
                Button(
                    attrs = Modifier
                        .backgroundColor(palette.custom.brandPrimary)
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .border(0.px)
                        .cursor(Cursor.Pointer)
                        .fontWeight(FontWeight.Bold)
                        .fontSize(18.px)
                        .padding(topBottom = 10.px, leftRight = 24.px)
                        .toAttrs {
                            onClick { formSubmitted = false }
                        }
                ) { Text(data.modalCloseButtonText) }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL
// ==========================================
data class EventRegistrationFormData(
    val sectionId: String? = null,
    val title: String = "Request Access",
    val description: String = "Join the vanguard of neurorehabilitation. Select your profile below to get started.",

    // Labels & Placeholders
    val fullNameLabel: String = "Full Name *",
    val fullNamePlaceholder: String = "Jane Doe",
    val emailLabel: String = "Email Address *",
    val emailPlaceholder: String = "jane@example.com",
    val clinicNameLabel: String = "Clinic / Organization Name *",
    val clinicNamePlaceholder: String = "NeuroRehab Center",
    val addressLabel: String = "Address *",
    val addressPlaceholder: String = "123 Recovery Lane",
    val cityLabel: String = "City *",
    val cityPlaceholder: String = "Toronto",
    val provinceLabel: String = "Province / State *",
    val postalCodeLabel: String = "Postal / Zip Code *",
    val postalCodePlaceholder: String = "M5V 2L7",
    val messageLabel: String = "Message",
    val messagePlaceholder: String = "Tell us about your requirements...",

    // Dynamic Consent Texts
    val patientConsentText: String = "I consent to IRegained Inc. collecting my contact information to communicate with me regarding my finding a clinic, general information or scheduling a trial of MyHand® System.",
    val clinicConsentText: String = "I consent to IRegained Inc. collecting my contact information to communicate with me regarding a MyHand® System showcase event or commercial partnership.",

    // Buttons & Alerts
    val buttonText: String = "Submit Request",
    val buttonSubmittingText: String = "Submitting...",
    val alertErrorMessage: String = "Submission connection failed. Please attempt your submission again.",

    // Success Modal
    val modalIconText: String = "✓",
    val modalTitle: String = "Request Submitted",
    val modalDescription: String = "Thank you for reaching out. We have successfully received your request. A team specialist will contact you shortly.",
    val modalCloseButtonText: String = "Close",

    // Endpoint
    val formProcessingEndpoint: String = "https://script.google.com/macros/s/AKfycbzk4P6E6CbUbS9yFjbF62-PKzcm9iHtcZJQDXw-RsAlQh9CA9nLvC13HBiwszFt8pIoKg/exec"
)

val defaultEventRegistrationData = EventRegistrationFormData()